import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, PenLine, Mail } from "lucide-react";
import { fetchBlogPosts } from "@/lib/api";
import { OrnamentalDivider } from "@/components/OrnamentalDivider";
import { IMAGES } from "@/data/content";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchBlogPosts();
        setPosts(data || []);
      } catch (err) {
        console.error("Failed to load public blog posts:", err);
      }
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div data-testid="blog-page">
      {/* HERO */}
      <section className="relative h-[38vh] min-h-[320px] overflow-hidden">
        <img
          src={IMAGES.paperTexture}
          alt="Vintage Melbourne map"
          className="absolute inset-0 w-full h-full object-cover sepia-soft"
        />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-12">
          <div className="stamp bg-[#FDFBF7]/85 w-fit">The Journal</div>
          <h1 className="serif text-white text-5xl md:text-6xl mt-4">
            {posts.length > 0 ? "Stories from Martin." : "Blogs to come."}
          </h1>
          <p className="text-[#F5EFEB] mt-3 max-w-2xl">
            {posts.length > 0
              ? "The tales between the tours — clocks that don't tell the time, laneways nobody remembers, and the Melbourne that lives quietly under our feet."
              : "Once our walkers start sharing their favourite Melbourne moments, they'll live right here."}
          </p>
        </div>
      </section>

      {loading && (
        <section className="max-w-6xl mx-auto px-6 py-20 text-center">
          <Loader2 className="mx-auto animate-spin text-[#6E2638]" size={32} />
        </section>
      )}

      {!loading && posts.length === 0 && (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 py-20 text-center" data-testid="blog-empty">
          <PenLine size={56} className="mx-auto text-[#D4AF37]" />
          <h2 className="serif text-3xl md:text-4xl text-[#6E2638] mt-6">
            The Journal is warming up.
          </h2>
          <OrnamentalDivider />
          <p className="text-[#1A1817]/85 leading-relaxed">
            We're collecting first-hand stories from our tour guests — the moments that surprised them, the corners of Melbourne they'll never forget, and the questions Martin still can't answer.
          </p>
          <p className="text-[#1A1817]/85 mt-5 leading-relaxed">
            Take the walk, then send us your story at{" "}
            <a
              href="mailto:Mmmwt35@gmail.com"
              className="text-[#6E2638] font-semibold underline decoration-[#D4AF37] underline-offset-4"
            >
              Mmmwt35@gmail.com
            </a>
            {" "}— the best ones will land right here.
          </p>

          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/individual" className="btn-primary">
              Book the walk <ArrowRight size={16} />
            </Link>
            <Link to="/quiz" className="btn-outline-gold">
              Try the Melbourne quiz
            </Link>
            <a href="mailto:Mmmwt35@gmail.com" className="btn-outline-gold" data-testid="blog-share-story">
              <Mail size={16} /> Share a story
            </a>
          </div>
        </section>
      )}

      {!loading && posts.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <OrnamentalDivider label="The Journal" />
          <div className="grid md:grid-cols-2 gap-10 mt-10">
            {posts.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="group block card-warm rounded-2xl overflow-hidden"
              >
                {p.cover_image_url && (
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={p.cover_image_url}
                      alt={p.title}
                      className="w-full h-full object-cover sepia-soft group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.24em] text-[#1E4637]">
                    {new Date(p.created_at).toLocaleDateString("en-AU", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <h2 className="serif text-2xl text-[#6E2638] mt-2 leading-tight">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="text-sm mt-3 text-[#1A1817]/80 leading-relaxed">
                      {p.excerpt}
                    </p>
                  )}
                  <div className="mt-4 inline-flex items-center gap-2 text-[#6E2638] text-sm font-semibold group-hover:gap-3 transition-all">
                    Read the story <ArrowRight size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
